'use client';

import { ProductCard } from '@components/Product/ProductCard';
import { ProductRow as ProductRowUI } from '@components/Product/ProductRow';
import { ProductRowHeading } from '@components/Product/ProductRowHeading';
import { getProductCardTabletClasses } from '@components/Product/productCardTabletStyles';
import { TrackPromotionImpression } from '@components/TrackPromotionImpression/TrackPromotionImpression';
import { usePromoTrackingEvents } from '@components/TrackPromotionImpression/usePromoTrackingEvents';
import { useEntitlements } from '@contexts/EntitlementsContext';
import { useProductLists } from '@contexts/ProductListsContext';
import { useRouter } from '@contexts/Router/RouterContext';
import { useFlags } from '@lib/flags/amplitude/client';
import { FEATURE_FLAG_ON, Flags } from '@lib/flags/flagConstants';
import { ProductRow as ProductRowType } from '@lib/types/cms/ProductRow';
import { longTextToProductIds } from '@lib/util/longTextToProductIds';
import c from 'classnames';
import { useEffect, useRef } from 'react';
import { mapToProductCardProps } from './mapToProductCardProps';
import { usePackagesByIds } from './usePackagesByIds';

export type ProductRowProps = {
  data: ProductRowType;
  position: number;
};

const ProductRow = ({ data, position }: ProductRowProps) => {
  const { locale } = useRouter();
  const {
    title,
    description,
    text,
    textPlain,
    linkHref,
    linkTitle,
    productIdList,
    titleInternal,
    trackAsPromo,
    productColumnCount,
  } = data;

  const DEFAULT_NUMBER_OF_COLUMNS = 4;

  const link =
    linkHref && linkTitle ? { href: linkHref, label: linkTitle } : undefined;
  const productIDs = productIdList ? longTextToProductIds(productIdList) : [];
  const { data: products, error, isLoading } = usePackagesByIds(productIDs);
  const { getUserEntitlement } = useEntitlements();
  const { isProductInList, toggleProductList, fetchBatchProductMembership } =
    useProductLists();
  const { flags } = useFlags();
  const isProductListsEnabled =
    flags?.[Flags.PRODUCT_LISTS]?.value === FEATURE_FLAG_ON;
  const prevKeyRef = useRef('');

  useEffect(() => {
    if (!products || products.length === 0) return;

    const ids = products.map((p) => p.id).filter(Boolean);
    const key = ids.join(',');
    if (key && key !== prevKeyRef.current) {
      prevKeyRef.current = key;
      fetchBatchProductMembership(ids);
    }
  }, [products, fetchBatchProductMembership]);

  const { promoClickTrackingEvent, promoViewTrackingEvent } =
    usePromoTrackingEvents({
      title: titleInternal || '',
      typename: 'ProductRow',
      position,
      trackAsPromo: trackAsPromo ?? false,
      ctas: link?.label ? [link.label] : [],
    });

  if (isLoading || error || !products || products.length === 0) {
    return null;
  }

  const productCards = mapToProductCardProps(products, getUserEntitlement, {
    showFavorite: isProductListsEnabled,
    isProductInList,
    toggleProductList,
  });

  return (
    <TrackPromotionImpression
      onImpression={promoViewTrackingEvent}
      shouldTrack={trackAsPromo ?? false}
    >
      <ProductRowHeading
        title={title}
        description={description}
        text={text}
        textPlain={textPlain}
        link={link}
        onLinkClick={promoClickTrackingEvent}
      />
      <ProductRowUI gridCols={productColumnCount ?? DEFAULT_NUMBER_OF_COLUMNS}>
        {productCards.map((product, index) => (
          <ProductCard
            key={product.productId}
            className={c(
              // scroll row below md: the card min-width sizes the cards.
              // productCards.length (the mapped, isDefined-filtered list this
              // map iterates), not products.length — a raw count would
              // misalign the tablet hidden-card threshold if the mapper ever
              // filters an item.
              getProductCardTabletClasses(index, productCards.length, true)
            )}
            locale={locale}
            {...product}
          />
        ))}
      </ProductRowUI>
    </TrackPromotionImpression>
  );
};

export default ProductRow;
